import { useEffect } from "react";
import { vec3 } from "gl-matrix";
import { useShapeDiverStoreViewport } from "@AppBuilderShared/store/useShapeDiverStoreViewport";
import type { CeruleoAppvTheme } from "./useCeruleoHostedSessionBootstrap";

// Minimal slice of the ShapeDiver ICameraApi we rely on. Typed locally so this
// hook does not couple to a specific SDK export path.
type OffsetCameraApi = {
  position: vec3;
  target: vec3;
  project?: (p: vec3) => ArrayLike<number>;
  set?: (position: vec3, target: vec3) => unknown;
  zoomTo?: () => unknown;
};

// Width of the floating glass panel is min(380px, 34vw); the AUTO offset is half
// of that, so the model — centered by zoom-to-extents — visually lands in the
// open area to the LEFT of the panel. Mirrors the previous CSS auto value.
function resolveOffsetPx(
  cameraOffsetX: number | null | undefined,
): number {
  if (cameraOffsetX === null || cameraOffsetX === undefined) {
    return Math.min(190, 0.17 * window.innerWidth);
  }
  return cameraOffsetX;
}

// Pans the camera so the model shifts left on screen by `offsetPx`, keeping the
// canvas full-bleed. We measure how many screen pixels one world unit spans
// along the camera's right axis (via project), convert the pixel offset to a
// world distance, then translate BOTH camera position and target along that
// axis. Moving the camera right makes the scene appear to move left.
//
// Using project() makes this camera-type-agnostic (works for perspective and
// orthographic) without needing FOV/frustum math.
function applyCameraOffset(cam: OffsetCameraApi, offsetPx: number): void {
  if (!offsetPx || !cam.project || !cam.set) return;

  const view = vec3.sub(vec3.create(), cam.target, cam.position);
  vec3.normalize(view, view);
  const right = vec3.cross(vec3.create(), view, vec3.fromValues(0, 1, 0));
  if (vec3.length(right) < 1e-6) return; // camera looking straight up/down
  vec3.normalize(right, right);

  const pTarget = cam.project(cam.target);
  const pRight = cam.project(vec3.add(vec3.create(), cam.target, right));
  const pxPerWorld = Math.hypot(pRight[0] - pTarget[0], pRight[1] - pTarget[1]);
  if (pxPerWorld < 1e-6) return;

  const worldShift = offsetPx / pxPerWorld;
  const delta = vec3.scale(vec3.create(), right, worldShift);
  cam.set(
    vec3.add(vec3.create(), cam.position, delta),
    vec3.add(vec3.create(), cam.target, delta),
  );
}

/**
 * Frames the model (zoom-to-extents) on resize + initial load, and — in the
 * floating-overlay layout — applies a real ShapeDiver camera offset so the
 * model centers in the open area beside the glass panel. Supersedes
 * useRecenterCameraOnResize (it also handles the plain non-overlay recenter).
 *
 * Note: ShapeDiver auto-zooms on geometry change (parameter updates), which
 * re-centers without the offset. Re-applying the offset on output updates is a
 * follow-up; the reported symptoms (white band + no re-center on window flex)
 * are resize-driven and covered here.
 */
export function useApplyOverlayCameraOffset(
  theme: CeruleoAppvTheme | undefined,
): void {
  const isOverlay = theme?.layoutMode === "overlay";
  const cameraOffsetX = theme?.cameraOffsetX;

  useEffect(() => {
    let cancelled = false;

    const frameAll = async () => {
      const { viewports } = useShapeDiverStoreViewport.getState();
      for (const viewport of Object.values(viewports)) {
        const cam = (viewport as { camera?: OffsetCameraApi })?.camera;
        if (!cam?.zoomTo) continue;
        try {
          await cam.zoomTo();
          if (cancelled) return;
          if (isOverlay) applyCameraOffset(cam, resolveOffsetPx(cameraOffsetX));
        } catch {
          /* viewport not ready / transient — ignore */
        }
      }
    };

    // Initial: frame once a viewport with a camera exists.
    let framedInitial = false;
    const tryInitial = () => {
      if (framedInitial) return;
      const { viewports } = useShapeDiverStoreViewport.getState();
      const ready = Object.values(viewports).some(
        (v) => (v as { camera?: OffsetCameraApi })?.camera?.zoomTo,
      );
      if (ready) {
        framedInitial = true;
        setTimeout(() => void frameAll(), 150);
      }
    };
    tryInitial();
    const unsub = useShapeDiverStoreViewport.subscribe(tryInitial);

    // Re-frame on window resize (debounced).
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void frameAll(), 250);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      unsub();
      window.removeEventListener("resize", onResize);
      if (timer) clearTimeout(timer);
    };
  }, [isOverlay, cameraOffsetX]);
}
